import { useState, useEffect } from "react";
import { T } from "../../theme";
import { REL } from "../../theme";
import { usePeople } from "../../context/PeopleContext";
import { saveDisplayName } from "../../utils/displayName";
import { trackEvent } from "../../utils/analytics";
import { supabase } from "../../supabaseClient";
import apiFetch from "../../apiFetch.js";
import { fetchOAuthUrl, openNativeOAuth, isNative } from "../../utils/nativeOAuth.js";
import { Capacitor } from "@capacitor/core";

// ── Onboarding ───────────────────────────────────────────────────────────────
export function OnboardingContent({onNav}){
  const {user,userId}=usePeople();
  // userId may not be in context yet on first render; fall back to user.id from auth
  const effectiveUserId=userId||user?.id;
  const [step,setStep]=useState(0);
  const [displayName,setDisplayName]=useState(()=>{
    const prefix=user?.email?user.email.split("@")[0]:"";
    return (prefix&&prefix.toLowerCase()!=="morgan")?prefix:"";
  });
  const [saving,setSaving]=useState(false);

  // Step 3 (name) is saved before the calendar step so that if the user
  // clicks an OAuth provider and gets redirected, onboarding is already
  // considered complete on return (needsNamePrompt returns false).
  const steps=[
    {icon:"👋",title:"Welcome to orbit",     sub:"Thoughtful scheduling for everyone you love.",                                                          cta:"Get started"},
    {icon:"👥",title:"Your relationship web", sub:"Partners, family, friends, coparents — all in one place.",                                              cta:"Continue"},
    {icon:"🔒",title:"Privacy first",         sub:"You control exactly what each person can see.",                                                         cta:"Continue"},
    {icon:"👤",title:"What's your name?",     sub:"This is how you'll appear in greetings and to people you share with.",                                  cta:"Next",        isName:true},
    {icon:"📅",title:"Connect a calendar",    sub:"Sync your schedule so Orbit can find overlap time and send smart reminders.",                            cta:"Skip for now",isCalendar:true},
  ];

  const s=steps[step];
  const isLast=step===steps.length-1;
  const canAdvance=s.isName?(displayName.trim().length>0&&!saving):true;

  async function handleAdvance(){
    if(s.isName){
      if(saving||!displayName.trim()) return;
      setSaving(true);
      try{
        await saveDisplayName(displayName.trim(), effectiveUserId);
        trackEvent("Onboarding Name Set");
        // Request push notification permission on native right after the user
        // sets their name — this is the highest-trust moment in the flow.
        try{
          if(Capacitor.isNativePlatform()){
            const {PushNotifications}=await import("@capacitor/push-notifications");
            PushNotifications.requestPermissions().catch(()=>{});
          }
        }catch(_){}
      }finally{setSaving(false);}
      setStep(n=>n+1);
      return;
    }
    if(isLast){
      trackEvent("Onboarding Completed");
      onNav("home");
      return;
    }
    setStep(n=>n+1);
  }

  // Calendar providers shown on the last step.
  // Google and Outlook go through OAuth; iCloud needs credential entry in Settings.
  const CAL_PROVIDERS=[
    {id:"google",  icon:"🗓️",label:"Google Calendar",  color:"#34A853",
     onConnect:async()=>{trackEvent("Onboarding Completed");const {data:{session}}=await supabase.auth.getSession();if(session?.access_token){const providerUrl=await fetchOAuthUrl('/api/google/auth',session.access_token,{native:isNative()});if(isNative()){try{await openNativeOAuth(providerUrl);}catch{}}else{window.location.href=providerUrl;}}}},
    {id:"outlook", icon:"📧",label:"Microsoft Outlook", color:"#0078D4",
     onConnect:async()=>{trackEvent("Onboarding Completed");const {data:{session}}=await supabase.auth.getSession();if(session?.access_token){const providerUrl=await fetchOAuthUrl('/api/outlook/auth',session.access_token,{native:isNative()});if(isNative()){try{await openNativeOAuth(providerUrl);}catch{}}else{window.location.href=providerUrl;}}}},
    {id:"icloud",  icon:"📅",label:"Apple / iCloud",    color:"#1C7BE0",
     onConnect:()=>{trackEvent("Onboarding Completed");onNav("settings");}},
  ];

  return(<div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:32,fontFamily:"'DM Sans',sans-serif"}}>
    <div style={{maxWidth:360,width:"100%",textAlign:"center",animation:"fadeUp .4s ease"}}>
      <div style={{width:72,height:72,borderRadius:20,background:`linear-gradient(135deg,${T.partner},${T.coparent})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:28,margin:"0 auto 24px",boxShadow:`0 8px 32px ${T.partner}44`}}>{s.icon}</div>
      <h1 style={{fontSize:28,fontFamily:"'Crimson Pro',serif",fontWeight:300,color:T.ink,letterSpacing:"-0.02em",marginBottom:10}}>{s.title}</h1>
      <p style={{fontSize:14,color:T.muted,lineHeight:1.7,marginBottom:(s.isName||s.isCalendar)?16:32}}>{s.sub}</p>

      {s.isName&&(
        <>
          <input
            autoFocus
            aria-label="Your display name"
            value={displayName}
            onChange={e=>setDisplayName(e.target.value)}
            onKeyDown={e=>e.key==="Enter"&&canAdvance&&handleAdvance()}
            placeholder="Your first name"
            style={{width:"100%",padding:"13px 14px",borderRadius:12,border:`1.5px solid ${displayName.trim()?T.partner+"60":T.edge}`,background:T.mist,fontSize:15,color:T.ink,outline:"none",textAlign:"center",marginBottom:8,boxSizing:"border-box"}}
          />
          <div style={{height:12}}/>
        </>
      )}

      {s.isCalendar&&(
        <div style={{marginBottom:16}}>
          {CAL_PROVIDERS.map(p=>(
            <button key={p.id} onClick={p.onConnect} style={{display:"flex",alignItems:"center",gap:12,width:"100%",padding:"12px 16px",borderRadius:13,border:`1px solid ${T.edge}`,background:T.surface,cursor:"pointer",marginBottom:8,textAlign:"left",boxSizing:"border-box"}}>
              <span style={{fontSize:20}}>{p.icon}</span>
              <span style={{fontSize:14,fontWeight:500,color:T.ink,flex:1}}>{p.label}</span>
              <span style={{fontSize:13,color:p.color,fontWeight:600}}>Connect →</span>
            </button>
          ))}
        </div>
      )}

      <div style={{display:"flex",gap:6,justifyContent:"center",marginBottom:28}}>
        {steps.map((_,i)=><div key={i} style={{height:5,borderRadius:3,width:i===step?22:6,background:i<=step?T.clay:T.edge,transition:"all .25s"}}/>)}
      </div>
      <button
        onClick={handleAdvance}
        disabled={!canAdvance}
        style={{width:"100%",padding:"14px",borderRadius:14,background:`linear-gradient(135deg,${T.bark},${T.clay})`,border:"none",color:"white",fontSize:15,fontWeight:600,cursor:canAdvance?"pointer":"not-allowed",boxShadow:`0 4px 20px ${T.bark}44`,opacity:canAdvance?1:0.45}}
      >{saving?"Saving…":s.cta}</button>
      {step>0&&<button onClick={()=>setStep(n=>n-1)} style={{marginTop:12,background:"none",border:"none",color:T.muted,fontSize:12,cursor:"pointer"}}>← Back</button>}
    </div>
  </div>);
}

// ── Second User Onboarding ──────────────────────────────────────────────────
function deriveIni(name){
  if(!name) return "?";
  const parts=name.trim().split(/\s+/).filter(Boolean);
  if(parts.length===1) return parts[0].slice(0,2).toUpperCase();
  return (parts[0][0]+parts[parts.length-1][0]).toUpperCase();
}
const SU_PROPOSED_AGREEMENTS=[
  {id:"a2",type:"notice", icon:"🔔",color:T.friend,  title:"24-hour notice",    detail:"For any date request",            description:"Give each other at least 24 hours notice when requesting time.",adjustable:false},
  {id:"a3",type:"fairness",icon:"⚖️",color:T.amber,  title:"Monthly check-in", detail:"Review fairness together",         description:"Once a month, review the balance of time and adjust targets if needed.",adjustable:false},
];
const SU_MORGAN_SHARING={
  level:"availability",
  preview:[
    {day:"Mon",slots:["Free 7–10 PM"]},{day:"Tue",slots:["Free 6–11 PM"]},
    {day:"Wed",slots:["Busy all evening"]},{day:"Thu",slots:["Free 7–10 PM"]},
    {day:"Fri",slots:["Free after 6 PM"]},{day:"Sat",slots:["Mostly free"]},
    {day:"Sun",slots:["Free until 8 PM"]},
  ],
};
const SU_PRIVACY_LEVELS=[
  {id:"full",        label:"Full details", desc:"Exact times, titles & notes",  icon:"📋"},
  {id:"availability",label:"Availability", desc:"Free / busy windows only",     icon:"📊"},
  {id:"busy",        label:"Busy blocks",  desc:"Only when unavailable",        icon:"🔶"},
  {id:"hidden",      label:"Hidden",       desc:"No calendar info shared",      icon:"🚫"},
];
const SU_FLOW_STEPS=[
  {id:"profile",    label:"Your profile"},
  {id:"reltype",    label:"Relationship"},
  {id:"privacy-see",label:"What you'll see"},
  {id:"privacy-set",label:"What you share"},
  {id:"availability",label:"Availability"},
  {id:"agreements", label:"Agreements"},
  {id:"done",       label:"All set"},
];

function SUMorganAvatar({size=36,name=null,photo=null}){
  if(photo) return(<img src={photo} alt="" style={{width:size,height:size,borderRadius:"50%",objectFit:"cover",flexShrink:0,boxShadow:`0 2px 10px ${T.partner}44`}}/>);
  const ini=name?deriveIni(name):"MO";
  return(<div style={{width:size,height:size,borderRadius:"50%",background:`linear-gradient(135deg,${T.partner},${T.coparent})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:size*.3,fontWeight:700,color:"white",fontFamily:"'Crimson Pro',serif",flexShrink:0,boxShadow:`0 2px 10px ${T.partner}44`}}>{ini}</div>);
}
function SUAlexAvatar({size=36,name=null}){
  const ini=name?deriveIni(name):"AK";
  return(<div style={{width:size,height:size,borderRadius:"50%",background:T.partner+"20",border:`2px solid ${T.partner}55`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:size*.3,fontWeight:700,color:T.partner,fontFamily:"'Crimson Pro',serif",flexShrink:0}}>{ini}</div>);
}
function SUProgressDots({total,current}){
  return(<div style={{display:"flex",gap:5,alignItems:"center"}}>
    {Array.from({length:total}).map((_,i)=>(
      <div key={i} style={{height:5,borderRadius:3,width:i===current?22:6,background:i<=current?T.partner:T.edge,transition:"all .25s ease"}}/>
    ))}
  </div>);
}
function SUStepShell({stepIdx,children}){
  const total=SU_FLOW_STEPS.length-1;
  return(<div style={{flex:1,display:"flex",flexDirection:"column",animation:"fadeUp .25s ease"}}>
    <div style={{padding:"12px 20px 0",flexShrink:0}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
        <SUProgressDots total={total} current={Math.min(stepIdx,total-1)}/>
        <span style={{fontSize:11,color:T.muted}}>{Math.min(stepIdx+1,total)} of {total}</span>
      </div>
    </div>
    {children}
  </div>);
}
function SULinkEntry({onContinue,inviterName=null,inviterPhoto=null,inviterRelType=null}){
  const name=inviterName||"Morgan";
  const relLabel=inviterRelType?(REL[inviterRelType]?.label||inviterRelType):"partner";
  return(<div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"28px 24px",animation:"fadeUp .3s ease"}}>
    <div style={{width:56,height:56,borderRadius:14,background:T.sage+"18",border:`1px solid ${T.sage}30`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,marginBottom:18}}>✓</div>
    <h2 style={{fontSize:22,fontFamily:"'Crimson Pro',serif",fontWeight:300,color:T.ink,letterSpacing:"-0.02em",marginBottom:8,textAlign:"center"}}>Link verified</h2>
    <p style={{fontSize:13,color:T.muted,textAlign:"center",lineHeight:1.7,marginBottom:24}}>{name}'s invite link is valid. Let's set up your account.</p>
    <div style={{width:"100%",padding:"12px 14px",borderRadius:12,background:T.mist,border:`1px solid ${T.edge}`,marginBottom:24}}>
      <div style={{display:"flex",alignItems:"center",gap:10}}>
        <SUMorganAvatar size={30} name={inviterName} photo={inviterPhoto}/>
        <div><p style={{fontSize:12,fontWeight:600,color:T.ink}}>{name}</p><p style={{fontSize:11,color:T.muted}}>Inviting you as a {relLabel} ◎</p></div>
      </div>
    </div>
    <button onClick={onContinue} style={{width:"100%",padding:"14px",borderRadius:14,background:T.bark,border:"none",color:T.parchment,fontSize:15,fontWeight:600,cursor:"pointer",boxShadow:`0 4px 18px ${T.bark}33`}}>Accept & continue →</button>
  </div>);
}
function SUEmailEntry({onContinue}){
  const [email,setEmail]=useState("");
  const [sent,setSent]=useState(false);
  if(sent) return(<div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"28px 24px",textAlign:"center",animation:"fadeUp .3s ease"}}>
    <div style={{fontSize:40,marginBottom:14}}>📬</div>
    <h3 style={{fontSize:20,fontFamily:"'Crimson Pro',serif",fontWeight:300,marginBottom:8}}>Check {email}</h3>
    <p style={{fontSize:13,color:T.muted,lineHeight:1.7,marginBottom:24}}>We sent a magic link. Click it to verify and continue setup.</p>
    <button onClick={onContinue} style={{width:"100%",padding:"12px",borderRadius:12,background:T.clay,border:"none",color:"white",fontSize:13,fontWeight:600,cursor:"pointer"}}>✓ Simulate email click</button>
    <button onClick={()=>setSent(false)} style={{marginTop:10,background:"none",border:"none",color:T.muted,fontSize:12,cursor:"pointer"}}>Resend</button>
  </div>);
  return(<div style={{flex:1,display:"flex",flexDirection:"column",padding:"28px 24px",animation:"fadeUp .3s ease"}}>
    <div style={{fontSize:26,marginBottom:14}}>📧</div>
    <h2 style={{fontSize:22,fontFamily:"'Crimson Pro',serif",fontWeight:300,color:T.ink,marginBottom:6}}>Check your email</h2>
    <p style={{fontSize:13,color:T.muted,lineHeight:1.7,marginBottom:18}}>Enter the email where Morgan sent your invite.</p>
    <input value={email} onChange={e=>setEmail(e.target.value)} placeholder="your@email.com" style={{width:"100%",padding:"12px 14px",borderRadius:12,border:`1.5px solid ${T.edge}`,background:T.mist,fontSize:14,color:T.ink,fontFamily:"'DM Sans',sans-serif",marginBottom:12}}/>
    <button onClick={()=>email.includes("@")&&setSent(true)} style={{width:"100%",padding:"13px",borderRadius:12,background:email.includes("@")?T.bark:T.edgeMid,border:"none",color:"white",fontSize:14,fontWeight:600,cursor:"pointer"}}>Send verification link</button>
  </div>);
}
function SUQREntry({onContinue}){
  const [scanning,setScanning]=useState(false);
  const [scanned,setScanned]=useState(false);
  useEffect(()=>{if(scanning){const t=setTimeout(()=>setScanned(true),1600);return()=>clearTimeout(t);}},[scanning]);
  return(<div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",padding:"24px 24px",animation:"fadeUp .3s ease"}}>
    <div style={{fontSize:24,marginBottom:10}}>📷</div>
    <h2 style={{fontSize:22,fontFamily:"'Crimson Pro',serif",fontWeight:300,color:T.ink,marginBottom:6,textAlign:"center"}}>Scan Morgan's QR code</h2>
    <p style={{fontSize:13,color:T.muted,textAlign:"center",lineHeight:1.7,marginBottom:18}}>Ask Morgan to show their Orbit QR code</p>
    <div style={{width:200,height:200,borderRadius:14,background:scanned?T.sage+"18":T.bark,border:`2px solid ${scanned?T.sage:T.edgeMid}`,display:"flex",alignItems:"center",justifyContent:"center",marginBottom:18,position:"relative",overflow:"hidden",transition:"all .3s"}}>
      {scanned?<div style={{textAlign:"center",animation:"popIn .3s ease"}}><div style={{fontSize:32,marginBottom:6}}>✓</div><p style={{fontSize:13,fontWeight:600,color:T.sage}}>Code scanned!</p></div>
      :scanning?<div style={{textAlign:"center"}}><div style={{position:"absolute",left:0,right:0,height:2,background:T.clay+"CC",animation:"scanLine 1.2s ease-in-out infinite"}}/><p style={{fontSize:12,color:T.edgeMid}}>Scanning…</p></div>
      :<div style={{textAlign:"center",padding:16}}><div style={{display:"grid",gridTemplateColumns:"repeat(8,1fr)",gap:2,marginBottom:8}}>{Array.from({length:64}).map((_,i)=><div key={i} style={{width:8,height:8,borderRadius:1,background:((i*7+3)%5>2)?"white":"transparent"}}/>)}</div><p style={{fontSize:11,color:T.edgeMid}}>Point camera at code</p></div>}
    </div>
    {!scanned?<button onClick={()=>setScanning(true)} disabled={scanning} style={{width:"100%",maxWidth:260,padding:"13px",borderRadius:12,background:scanning?T.edgeMid:T.bark,border:"none",color:"white",fontSize:14,fontWeight:600,cursor:scanning?"default":"pointer"}}>{scanning?"Scanning…":"Start camera"}</button>
    :<button onClick={onContinue} style={{width:"100%",maxWidth:260,padding:"13px",borderRadius:12,background:T.bark,border:"none",color:"white",fontSize:14,fontWeight:600,cursor:"pointer",animation:"fadeUp .3s ease"}}>Continue →</button>}
  </div>);
}
function SUStepProfile({onNext}){
  const {user,userId}=usePeople();
  const effectiveId=userId||user?.id;
  const [name,setName]=useState(()=>{
    const m=user?.user_metadata||{};
    return m.display_name||(user?.email?user.email.split("@")[0]:"");
  });
  const [saving,setSaving]=useState(false);
  async function handleNext(){
    if(!name.trim()||saving) return;
    setSaving(true);
    try{await saveDisplayName(name.trim(), effectiveId);}catch(_){}finally{setSaving(false);}
    onNext();
  }
  const ini=name?deriveIni(name):"?";
  return(<SUStepShell stepIdx={0}>
    <div style={{flex:1,overflowY:"auto",padding:"16px 20px"}}>
      <h2 style={{fontSize:22,fontFamily:"'Crimson Pro',serif",fontWeight:300,color:T.ink,marginBottom:4}}>Let's set up your profile</h2>
      <p style={{fontSize:13,color:T.muted,marginBottom:20}}>They'll see this when you connect</p>
      <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:22}}>
        <div style={{width:60,height:60,borderRadius:"50%",background:T.partner+"20",border:`2px solid ${T.partner}55`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,fontWeight:700,color:T.partner,fontFamily:"'Crimson Pro',serif",flexShrink:0}}>{ini}</div>
      </div>
      <div>
        <label style={{fontSize:11,color:T.muted,display:"block",marginBottom:5,textTransform:"uppercase",letterSpacing:"0.07em"}}>Your name</label>
        <input
          autoFocus
          value={name}
          onChange={e=>setName(e.target.value)}
          onKeyDown={e=>e.key==="Enter"&&name.trim()&&handleNext()}
          placeholder="Your first name"
          style={{width:"100%",padding:"11px 13px",borderRadius:11,border:`1.5px solid ${name.trim()?T.partner+"60":T.edge}`,background:T.mist,fontSize:14,color:T.ink,fontFamily:"'DM Sans',sans-serif",boxSizing:"border-box"}}
        />
      </div>
    </div>
    <div style={{padding:"12px 20px 28px",flexShrink:0,borderTop:`1px solid ${T.edge}`}}>
      <button onClick={handleNext} disabled={!name.trim()||saving} style={{width:"100%",padding:"14px",borderRadius:14,background:name.trim()&&!saving?T.bark:T.edgeMid,border:"none",color:T.parchment,fontSize:15,fontWeight:600,cursor:name.trim()&&!saving?"pointer":"default",boxShadow:name.trim()?`0 4px 18px ${T.bark}33`:"none"}}>{saving?"Saving…":"Continue →"}</button>
    </div>
  </SUStepShell>);
}
function SUStepRelType({onNext,inviterName=null,inviterPhoto=null,inviterRelType=null}){
  const name=inviterName||"Morgan";
  const theirRelLabel=inviterRelType?(REL[inviterRelType]?.label||inviterRelType):"Partner";
  const theirRelIcon=inviterRelType?(REL[inviterRelType]?.icon||"◎"):"◎";
  const [chosen,setChosen]=useState(null);
  return(<SUStepShell stepIdx={1}>
    <div style={{flex:1,overflowY:"auto",padding:"16px 20px"}}>
      <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 13px",borderRadius:12,background:T.panel,border:`1px solid ${T.edge}`,marginBottom:18}}>
        <SUMorganAvatar size={32} name={inviterName} photo={inviterPhoto}/>
        <div><p style={{fontSize:12,fontWeight:600,color:T.ink}}>{name} added you as</p><p style={{fontSize:13,fontWeight:700,color:T.partner,marginTop:2}}>{theirRelIcon} {theirRelLabel}</p></div>
      </div>
      <h2 style={{fontSize:22,fontFamily:"'Crimson Pro',serif",fontWeight:300,color:T.ink,marginBottom:4}}>How do <em>you</em> know {name}?</h2>
      <p style={{fontSize:13,color:T.muted,marginBottom:18,lineHeight:1.6}}>You can set a different type from your side — Orbit uses both perspectives.</p>
      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        {Object.entries(REL).map(([key,cfg])=>(
          <button key={key} onClick={()=>setChosen(key)} style={{display:"flex",alignItems:"center",gap:12,padding:"13px 15px",borderRadius:14,cursor:"pointer",background:chosen===key?cfg.color+"14":T.mist,border:`1.5px solid ${chosen===key?cfg.color:T.edge}`,transition:"all .14s",textAlign:"left"}}>
            <span style={{fontSize:20,color:cfg.color}}>{cfg.icon}</span>
            <div style={{flex:1}}><p style={{fontSize:14,fontWeight:600,color:chosen===key?T.ink:T.bark}}>{cfg.label}</p><p style={{fontSize:11,color:T.muted}}>{cfg.desc||cfg.plural}</p></div>
            {chosen===key&&<span style={{fontSize:12,color:cfg.color}}>✓</span>}
          </button>
        ))}
      </div>
      {chosen&&inviterRelType&&chosen!==inviterRelType&&(
        <div style={{marginTop:14,padding:"11px 13px",borderRadius:11,background:T.amber+"0C",border:`1px solid ${T.amber}30`,animation:"fadeUp .2s ease"}}>
          <p style={{fontSize:12,color:T.bark,lineHeight:1.6}}>◎ {name} sees you as a <strong>{theirRelLabel}</strong>. You've chosen <strong>{REL[chosen]?.label}</strong>. Features available to the higher-trust type apply.</p>
        </div>
      )}
    </div>
    <div style={{padding:"12px 20px 28px",flexShrink:0,borderTop:`1px solid ${T.edge}`}}>
      <button onClick={()=>chosen&&onNext(chosen)} style={{width:"100%",padding:"14px",borderRadius:14,background:chosen?T.bark:T.edgeMid,border:"none",color:"white",fontSize:15,fontWeight:600,cursor:chosen?"pointer":"default",transition:"background .15s"}}>Continue →</button>
    </div>
  </SUStepShell>);
}
function SUStepPrivacySee({onNext,inviterName=null,inviterPhoto=null}){
  const name=inviterName||"Morgan";
  const level=SU_PRIVACY_LEVELS.find(l=>l.id===SU_MORGAN_SHARING.level);
  return(<SUStepShell stepIdx={2}>
    <div style={{flex:1,overflowY:"auto",padding:"16px 20px"}}>
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
        <SUMorganAvatar size={38} name={inviterName} photo={inviterPhoto}/>
        <div>
          <h2 style={{fontSize:20,fontFamily:"'Crimson Pro',serif",fontWeight:300,color:T.ink}}>Here's what {name} is sharing</h2>
          <p style={{fontSize:12,color:T.muted}}>Their current privacy setting for you</p>
        </div>
      </div>
      <div style={{display:"inline-flex",alignItems:"center",gap:8,padding:"8px 14px",borderRadius:12,marginBottom:16,background:T.partner+"12",border:`1px solid ${T.partner}30`}}>
        <span style={{fontSize:16,color:T.partner}}>{level?.icon}</span>
        <div><p style={{fontSize:13,fontWeight:700,color:T.partner}}>{level?.label}</p><p style={{fontSize:11,color:T.muted}}>{level?.desc}</p></div>
      </div>
      <p style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>This week's availability preview</p>
      <div style={{background:T.surface,borderRadius:13,border:`1px solid ${T.edge}`,overflow:"hidden",marginBottom:14}}>
        {SU_MORGAN_SHARING.preview.map((d,i)=>(
          <div key={d.day} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 13px",borderBottom:i<SU_MORGAN_SHARING.preview.length-1?`1px solid ${T.edge}`:"none",background:i===1?"#FDF0E6":T.surface}}>
            <span style={{fontSize:11,fontWeight:700,color:T.muted,width:26}}>{d.day}</span>
            <div style={{flex:1,display:"flex",gap:5,flexWrap:"wrap"}}>
              {d.slots.map((s,si)=><span key={si} style={{fontSize:11,padding:"2px 8px",borderRadius:8,background:s.includes("Busy")?T.red+"12":T.sage+"14",color:s.includes("Busy")?T.red:T.sage,border:`1px solid ${s.includes("Busy")?T.red+"25":T.sage+"25"}`}}>{s}</span>)}
            </div>
            {i===1&&<span style={{fontSize:11,color:T.partner,background:T.partner+"14",padding:"1px 6px",borderRadius:6,fontWeight:600,flexShrink:0}}>Today</span>}
          </div>
        ))}
      </div>
      <div style={{padding:"10px 13px",borderRadius:11,background:T.mist,border:`1px solid ${T.edge}`}}>
        <p style={{fontSize:12,color:T.bark,lineHeight:1.6}}>◎ {name} can change their sharing level at any time in Settings.</p>
      </div>
    </div>
    <div style={{padding:"12px 20px 28px",flexShrink:0,borderTop:`1px solid ${T.edge}`}}>
      <button onClick={onNext} style={{width:"100%",padding:"14px",borderRadius:14,background:T.bark,border:"none",color:T.parchment,fontSize:15,fontWeight:600,cursor:"pointer"}}>Looks good →</button>
    </div>
  </SUStepShell>);
}
function SUStepPrivacySet({onNext,inviterName=null,inviterPhoto=null}){
  const name=inviterName||"Morgan";
  const [chosen,setChosen]=useState("availability");
  const previewText={full:"Free 7–10 PM · Dinner plans",availability:"Free 7–10 PM · Busy 10 PM+",busy:"Busy 10 PM–midnight",hidden:"· · · no info shared · · ·"}[chosen];
  return(<SUStepShell stepIdx={3}>
    <div style={{flex:1,overflowY:"auto",padding:"16px 20px"}}>
      <h2 style={{fontSize:20,fontFamily:"'Crimson Pro',serif",fontWeight:300,color:T.ink,marginBottom:4}}>What will you share with Morgan?</h2>
      <p style={{fontSize:13,color:T.muted,marginBottom:18,lineHeight:1.6}}>Choose what calendar info {name} can see. You can change this anytime in Settings.</p>
      <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:18}}>
        {SU_PRIVACY_LEVELS.map(l=>(
          <button key={l.id} onClick={()=>setChosen(l.id)} style={{display:"flex",alignItems:"center",gap:12,padding:"13px 15px",borderRadius:14,cursor:"pointer",background:chosen===l.id?T.partner+"10":T.mist,border:`1.5px solid ${chosen===l.id?T.partner:T.edge}`,transition:"all .14s",textAlign:"left"}}>
            <span style={{fontSize:16,color:chosen===l.id?T.partner:T.muted,width:20}}>{l.icon}</span>
            <div style={{flex:1}}><p style={{fontSize:14,fontWeight:600,color:chosen===l.id?T.ink:T.bark}}>{l.label}</p><p style={{fontSize:11,color:T.muted}}>{l.desc}</p></div>
            {chosen===l.id&&<span style={{fontSize:12,color:T.partner}}>✓</span>}
          </button>
        ))}
      </div>
      <div style={{padding:"11px 13px",borderRadius:12,background:T.surface,border:`1px solid ${T.edge}`,display:"flex",alignItems:"center",gap:10}}>
        <SUMorganAvatar size={28} name={inviterName} photo={inviterPhoto}/>
        <div><p style={{fontSize:11,color:T.muted,marginBottom:2}}>{name} sees your Thursday:</p><p style={{fontSize:12,fontWeight:600,color:chosen==="hidden"?T.muted:T.ink,fontStyle:chosen==="hidden"?"italic":"normal"}}>{previewText}</p></div>
      </div>
    </div>
    <div style={{padding:"12px 20px 28px",flexShrink:0,borderTop:`1px solid ${T.edge}`}}>
      <button onClick={()=>onNext(chosen)} style={{width:"100%",padding:"14px",borderRadius:14,background:T.bark,border:"none",color:T.parchment,fontSize:15,fontWeight:600,cursor:"pointer"}}>Set privacy →</button>
    </div>
  </SUStepShell>);
}
function SUStepAvailability({onNext}){
  const [connected,setConnected]=useState({});
  const [connecting,setConnecting]=useState(null);
  const [manualMode,setManualMode]=useState(false);
  const [grid,setGrid]=useState(()=>Object.fromEntries(["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map(d=>[d,{morning:false,afternoon:false,evening:true,night:false}])));
  const handleConnect=(id)=>{setConnecting(id);setTimeout(()=>{setConnecting(null);setConnected(prev=>({...prev,[id]:true}));},1600);};
  const toggleSlot=(day,slot)=>setGrid(g=>({...g,[day]:{...g[day],[slot]:!g[day][slot]}}));
  const SLOTS=[{id:"morning",label:"Morning",time:"7 AM–12 PM"},{id:"afternoon",label:"Afternoon",time:"12–5 PM"},{id:"evening",label:"Evening",time:"5–10 PM"},{id:"night",label:"Late",time:"10 PM+"}];
  return(<SUStepShell stepIdx={4}>
    <div style={{flex:1,overflowY:"auto",padding:"16px 20px"}}>
      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:4}}>
        <h2 style={{fontSize:20,fontFamily:"'Crimson Pro',serif",fontWeight:300,color:T.ink}}>Set your availability</h2>
        <button onClick={onNext} style={{padding:"5px 12px",borderRadius:10,background:T.mist,border:`1px solid ${T.edge}`,fontSize:11,color:T.muted,cursor:"pointer",flexShrink:0}}>Skip</button>
      </div>
      <p style={{fontSize:13,color:T.muted,marginBottom:18,lineHeight:1.6}}>Connect a calendar or set your general weekly availability so Morgan can find good overlap.</p>
      {!manualMode&&(
        <div style={{marginBottom:14}}>
          <p style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:10}}>Connect calendar</p>
          {[{id:"ios",icon:"📅",label:"Apple Calendar",color:"#1C7BE0"},{id:"google",icon:"🗓️",label:"Google Calendar",color:"#34A853"}].map(cal=>(
            <div key={cal.id} style={{display:"flex",alignItems:"center",gap:10,padding:"11px 13px",borderRadius:12,marginBottom:7,background:connected[cal.id]?T.sage+"0C":T.surface,border:`1px solid ${connected[cal.id]?T.sage+"40":T.edge}`,transition:"all .18s"}}>
              <span style={{fontSize:18}}>{cal.icon}</span>
              <p style={{fontSize:13,fontWeight:500,color:T.ink,flex:1}}>{cal.label}</p>
              {connected[cal.id]
                ?<span style={{fontSize:11,color:T.sage,fontWeight:600}}>✓ Connected</span>
                :<button onClick={()=>handleConnect(cal.id)} disabled={connecting===cal.id} style={{padding:"5px 12px",borderRadius:9,border:"none",cursor:"pointer",background:connecting===cal.id?T.edgeMid:cal.color,color:"white",fontSize:11,fontWeight:600}}>
                  {connecting===cal.id?"Connecting…":"Connect"}
                </button>}
            </div>
          ))}
          <button onClick={()=>setManualMode(true)} style={{background:"none",border:"none",fontSize:12,color:T.muted,cursor:"pointer",textDecoration:"underline",textUnderlineOffset:2}}>Set availability manually instead</button>
        </div>
      )}
      {manualMode&&(
        <div style={{animation:"fadeUp .2s ease"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
            <p style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em"}}>Weekly availability</p>
            <button onClick={()=>setManualMode(false)} style={{background:"none",border:"none",fontSize:11,color:T.muted,cursor:"pointer",textDecoration:"underline"}}>Use calendar instead</button>
          </div>
          <div style={{overflowX:"auto"}}>
            <div style={{minWidth:280}}>
              <div style={{display:"grid",gridTemplateColumns:"52px repeat(7,1fr)",gap:3,marginBottom:4}}>
                <div/>{["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map(d=><div key={d} style={{textAlign:"center",fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase"}}>{d}</div>)}
              </div>
              {SLOTS.map(slot=>(
                <div key={slot.id} style={{display:"grid",gridTemplateColumns:"52px repeat(7,1fr)",gap:3,marginBottom:3}}>
                  <div style={{fontSize:11,color:T.muted,textAlign:"right",paddingRight:6,paddingTop:4,fontWeight:600}}>{slot.label}</div>
                  {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map(day=>{
                    const on=grid[day][slot.id];
                    return(<div key={day} onClick={()=>toggleSlot(day,slot.id)} style={{height:24,borderRadius:5,cursor:"pointer",background:on?T.partner+"35":T.mist,border:`1px solid ${on?T.partner+"55":T.edge}`,transition:"all .13s"}}/>);
                  })}
                </div>
              ))}
            </div>
          </div>
          <p style={{fontSize:11,color:T.muted,marginTop:8}}>Tap to toggle · Colored = generally available</p>
        </div>
      )}
    </div>
    <div style={{padding:"12px 20px 28px",flexShrink:0,borderTop:`1px solid ${T.edge}`}}>
      <button onClick={onNext} style={{width:"100%",padding:"14px",borderRadius:14,background:T.bark,border:"none",color:T.parchment,fontSize:15,fontWeight:600,cursor:"pointer"}}>{Object.values(connected).some(Boolean)?"Continue →":"Continue anyway →"}</button>
    </div>
  </SUStepShell>);
}
function SUStepAgreements({onNext,inviterName=null,inviterPhoto=null}){
  const name=inviterName||"Morgan";
  const [states,setStates]=useState(Object.fromEntries(SU_PROPOSED_AGREEMENTS.map(a=>[a.id,{accepted:true,adjusted:false}])));
  const [adjusting,setAdjusting]=useState(null);
  return(<SUStepShell stepIdx={5}>
    <div style={{flex:1,overflowY:"auto",padding:"16px 20px"}}>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
        <SUMorganAvatar size={30} name={inviterName} photo={inviterPhoto}/>
        <h2 style={{fontSize:20,fontFamily:"'Crimson Pro',serif",fontWeight:300,color:T.ink}}>{name} proposed some agreements</h2>
      </div>
      <p style={{fontSize:13,color:T.muted,marginBottom:18,lineHeight:1.6}}>Accept, tweak, or decline each one. You can revisit these anytime in Settings.</p>
      {SU_PROPOSED_AGREEMENTS.map(a=>{
        const st=states[a.id];
        return(<div key={a.id} style={{marginBottom:12,padding:"14px 15px",borderRadius:14,background:st.accepted===false?T.mist:st.adjusted?T.amber+"08":T.surface,border:`1.5px solid ${st.accepted===false?T.edge:st.adjusted?T.amber+"40":a.color+"35"}`,transition:"all .18s"}}>
          <div style={{display:"flex",alignItems:"flex-start",gap:10,marginBottom:8}}>
            <div style={{width:34,height:34,borderRadius:9,flexShrink:0,background:a.color+"14",border:`1px solid ${a.color}30`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,color:a.color}}>{a.icon}</div>
            <div style={{flex:1}}>
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
                <p style={{fontSize:13,fontWeight:700,color:T.ink}}>{a.title}</p>
                {st.adjusted&&<span style={{fontSize:11,color:T.amber,background:T.amber+"18",padding:"1px 6px",borderRadius:8,border:`1px solid ${T.amber}30`,fontWeight:600}}>adjusted</span>}
                {st.accepted===false&&<span style={{fontSize:11,color:T.muted,background:T.mist,padding:"1px 6px",borderRadius:8,border:`1px solid ${T.edge}`}}>declined</span>}
              </div>
              <p style={{fontSize:11,color:T.muted}}>{a.detail}</p>
            </div>
          </div>
          <p style={{fontSize:12,color:T.bark,lineHeight:1.5,marginBottom:12,paddingLeft:44}}>{a.description}</p>
          {adjusting===a.id&&(
            <div style={{padding:"11px 12px",borderRadius:10,background:T.amber+"0C",border:`1px solid ${T.amber}30`,marginBottom:10,animation:"fadeUp .18s ease"}}>
              <p style={{fontSize:11,fontWeight:600,color:T.bark,marginBottom:8}}>Suggest a change</p>
              <textarea rows={2} placeholder="e.g. Tuesdays work better starting at 7 PM" style={{width:"100%",padding:"9px 11px",borderRadius:9,resize:"none",border:`1.5px solid ${T.edge}`,background:T.surface,fontSize:12,color:T.ink,fontFamily:"'DM Sans',sans-serif"}}/>
              <div style={{display:"flex",gap:7,marginTop:8}}>
                <button onClick={()=>setAdjusting(null)} style={{flex:1,padding:"7px",borderRadius:9,background:"transparent",border:`1px solid ${T.edge}`,color:T.muted,fontSize:11,cursor:"pointer"}}>Cancel</button>
                <button onClick={()=>{setStates(s=>({...s,[a.id]:{accepted:true,adjusted:true}}));setAdjusting(null);}} style={{flex:2,padding:"7px",borderRadius:9,background:T.amber,border:"none",color:"white",fontSize:11,fontWeight:600,cursor:"pointer"}}>Send suggestion</button>
              </div>
            </div>
          )}
          {adjusting!==a.id&&(
            <div style={{display:"flex",gap:7,paddingLeft:44}}>
              <button onClick={()=>setStates(s=>({...s,[a.id]:{accepted:false,adjusted:false}}))} style={{flex:1,padding:"7px",borderRadius:9,cursor:"pointer",background:st.accepted===false?T.red+"14":"transparent",border:`1px solid ${st.accepted===false?T.red+"35":T.edge}`,color:st.accepted===false?T.red:T.muted,fontSize:11,fontWeight:st.accepted===false?700:400}}>Decline</button>
              {a.adjustable&&<button onClick={()=>setAdjusting(a.id)} style={{flex:1,padding:"7px",borderRadius:9,cursor:"pointer",background:T.mist,border:`1px solid ${T.edge}`,color:T.bark,fontSize:11}}>Adjust ⟳</button>}
              <button onClick={()=>setStates(s=>({...s,[a.id]:{accepted:true,adjusted:false}}))} style={{flex:2,padding:"7px",borderRadius:9,cursor:"pointer",background:st.accepted===true&&!st.adjusted?a.color:T.mist,border:`1px solid ${st.accepted===true&&!st.adjusted?a.color:T.edge}`,color:st.accepted===true&&!st.adjusted?"white":T.bark,fontSize:11,fontWeight:st.accepted===true&&!st.adjusted?700:400}}>Accept ✓</button>
            </div>
          )}
        </div>);
      })}
    </div>
    <div style={{padding:"12px 20px 28px",flexShrink:0,borderTop:`1px solid ${T.edge}`}}>
      <button onClick={onNext} style={{width:"100%",padding:"14px",borderRadius:14,background:T.bark,border:"none",color:T.parchment,fontSize:15,fontWeight:600,cursor:"pointer"}}>Finish setup →</button>
    </div>
  </SUStepShell>);
}
function SUStepDone({relType,privacyLevel,onFinish,accepting=false,inviterName=null,inviterPhoto=null,inviteeName=null}){
  const relCfg=REL[relType||"partner"];
  const privCfg=SU_PRIVACY_LEVELS.find(l=>l.id===(privacyLevel||"availability"));
  const name=inviterName||"Morgan";
  return(<div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"28px 24px 48px",animation:"popIn .4s ease"}}>
    <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:24}}>
      <SUMorganAvatar size={52} name={inviterName} photo={inviterPhoto}/>
      <div style={{width:36,height:2,background:`linear-gradient(90deg,${T.partner},${T.coparent})`,borderRadius:2,boxShadow:`0 0 8px ${T.partner}55`}}/>
      <div style={{width:14,height:14,borderRadius:"50%",background:T.partner,boxShadow:`0 0 0 5px ${T.partner}25,0 0 0 10px ${T.partner}12`,animation:"pulse 2s ease infinite"}}/>
      <div style={{width:36,height:2,background:`linear-gradient(90deg,${T.coparent},${T.partner})`,borderRadius:2}}/>
      <SUAlexAvatar size={52} name={inviteeName}/>
    </div>
    <h1 style={{fontSize:28,fontFamily:"'Crimson Pro',serif",fontWeight:300,color:T.ink,textAlign:"center",marginBottom:8,lineHeight:1.2}}>You're connected<br/>with {name} ◎</h1>
    <p style={{fontSize:14,color:T.muted,textAlign:"center",lineHeight:1.7,marginBottom:22,maxWidth:300}}>Orbit will now help you find overlap time and keep things balanced.</p>
    <div style={{width:"100%",maxWidth:300,background:T.surface,borderRadius:14,border:`1px solid ${T.edge}`,overflow:"hidden",marginBottom:24,boxShadow:"0 2px 12px rgba(60,40,20,0.06)"}}>
      {[
        {label:`You added ${name} as`,value:`${relCfg.icon} ${relCfg.label}`,color:relCfg.color},
        {label:"You're sharing",value:`${privCfg?.icon} ${privCfg?.label}`,color:T.muted},
        {label:"Agreements reviewed",value:`${SU_PROPOSED_AGREEMENTS.length} total`,color:T.sage},
      ].map((row,i)=>(
        <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"11px 16px",borderBottom:i<2?`1px solid ${T.edge}`:"none"}}>
          <span style={{fontSize:12,color:T.muted}}>{row.label}</span>
          <span style={{fontSize:12,fontWeight:700,color:row.color}}>{row.value}</span>
        </div>
      ))}
    </div>
    <button onClick={onFinish} disabled={accepting} style={{width:"100%",maxWidth:300,padding:"15px",borderRadius:16,background:accepting?T.edgeMid:`linear-gradient(135deg,${T.bark},${T.partner})`,border:"none",color:"white",fontSize:15,fontWeight:700,cursor:accepting?"default":"pointer",boxShadow:accepting?"none":`0 6px 28px ${T.bark}44`}}>{accepting?"Connecting…":"Open orbit →"}</button>
    <p style={{fontSize:11,color:T.muted,marginTop:12}}>{name} will get a notification that you've connected</p>
  </div>);
}

export function SecondUserContent({onNav,inviteToken}){
  const {user}=usePeople();
  // If a token is present, skip straight to "verifying" while the API call is in flight
  const [phase,setPhase]=useState(inviteToken?"verifying":"entry-select");
  const [entryMethod,setEntryMethod]=useState(inviteToken?"link":null);
  const [flowStep,setFlowStep]=useState(0);
  const [relType,setRelType]=useState(null);
  const [privacyLevel,setPrivacyLevel]=useState(null);
  const [inviterInfo,setInviterInfo]=useState(null); // populated by verify API
  const [inviteError,setInviteError]=useState(null);
  const [accepting,setAccepting]=useState(false);

  // Verify the invite token once on mount
  useEffect(()=>{
    if(!inviteToken) return;
    fetch(`/api/invite/verify?token=${encodeURIComponent(inviteToken)}`)
      .then(r=>r.json())
      .then(data=>{
        if(data.valid){
          setInviterInfo(data);
          setPhase("entry-flow"); // auto-advance; SULinkEntry shows "verified" state
        } else {
          const msg=data.reason==="expired"?"This invite link has expired."
            :data.reason==="used"?"This invite link has already been used."
            :"This invite link is invalid.";
          setInviteError(msg);
          setPhase("entry-select"); // fall through to manual entry
        }
      })
      .catch(()=>setPhase("entry-select"));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  const inviterName=inviterInfo?.from_display_name||null;
  const inviterPhoto=inviterInfo?.from_photo||null;
  const inviterRelType=inviterInfo?.relationship_type||null;
  const inviteeName=user?.user_metadata?.display_name||(user?.email?user.email.split("@")[0]:null);

  const handleEntry=(method)=>{setEntryMethod(method);setPhase("entry-flow");};
  const handleEntryComplete=()=>setPhase("onboarding");
  const advanceFlow=(data)=>{
    if(flowStep===1&&data) setRelType(data);
    if(flowStep===3&&data) setPrivacyLevel(data);
    setFlowStep(s=>s+1);
  };

  // Called from SUStepDone — hit the accept endpoint then navigate home
  const handleFinish=async()=>{
    if(inviteToken){
      setAccepting(true);
      try{
        const {data:{session}}=await supabase.auth.getSession();
        if(session?.access_token){
          await apiFetch("/api/invite/accept",{
            method:"POST",
            headers:{"Content-Type":"application/json","Authorization":`Bearer ${session.access_token}`},
            body:JSON.stringify({token:inviteToken,relationship_type:relType||inviterRelType||"partner"}),
          });
        }
      }catch(_){}
      finally{setAccepting(false);}
    }
    trackEvent("Second User Onboarding Completed");
    onNav("home");
  };

  const renderEntryFlow=()=>{
    if(entryMethod==="link")  return <SULinkEntry  onContinue={handleEntryComplete} inviterName={inviterName} inviterPhoto={inviterPhoto} inviterRelType={inviterRelType}/>;
    if(entryMethod==="email") return <SUEmailEntry onContinue={handleEntryComplete}/>;
    if(entryMethod==="qr")    return <SUQREntry    onContinue={handleEntryComplete}/>;
    return null;
  };
  const renderFlowStep=()=>{
    if(flowStep===0) return <SUStepProfile    onNext={advanceFlow}/>;
    if(flowStep===1) return <SUStepRelType    onNext={advanceFlow} inviterName={inviterName} inviterPhoto={inviterPhoto} inviterRelType={inviterRelType}/>;
    if(flowStep===2) return <SUStepPrivacySee onNext={advanceFlow} inviterName={inviterName} inviterPhoto={inviterPhoto}/>;
    if(flowStep===3) return <SUStepPrivacySet onNext={advanceFlow} inviterName={inviterName} inviterPhoto={inviterPhoto}/>;
    if(flowStep===4) return <SUStepAvailability onNext={advanceFlow}/>;
    if(flowStep===5) return <SUStepAgreements onNext={advanceFlow} inviterName={inviterName} inviterPhoto={inviterPhoto}/>;
    return <SUStepDone relType={relType} privacyLevel={privacyLevel} onFinish={handleFinish} accepting={accepting} inviterName={inviterName} inviterPhoto={inviterPhoto} inviteeName={inviteeName}/>;
  };

  // ── Verifying spinner ──
  if(phase==="verifying") return(
    <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:24}}>
      <div style={{width:32,height:32,borderRadius:"50%",border:`3px solid ${T.partner}`,borderTopColor:"transparent",animation:"spin 0.8s linear infinite",marginBottom:16}}/>
      <p style={{fontSize:13,color:T.muted}}>Verifying invite…</p>
    </div>
  );

  // ── Entry select ──
  if(phase==="entry-select"){
    const name=inviterName||"someone";
    return(
      <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:24,animation:"fadeUp .4s ease"}}>
        <div style={{width:"100%",maxWidth:380}}>
          <div style={{textAlign:"center",marginBottom:28}}>
            <div style={{width:60,height:60,borderRadius:"50%",background:`linear-gradient(135deg,${T.partner},${T.coparent})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,margin:"0 auto 14px",boxShadow:`0 6px 24px ${T.partner}44`}}>◎</div>
            <h1 style={{fontSize:26,fontFamily:"'Crimson Pro',serif",fontWeight:300,color:T.ink,letterSpacing:"-0.02em",marginBottom:8}}>You've been invited<br/>to <em>orbit</em></h1>
            <p style={{fontSize:13,color:T.muted,lineHeight:1.6}}>{name} wants to connect with you for thoughtful, balanced scheduling.</p>
          </div>
          {inviteError&&<div style={{marginBottom:14,padding:"10px 12px",borderRadius:12,background:T.red+"10",border:`1px solid ${T.red}25`}}><p style={{fontSize:12,color:T.red}}>{inviteError}</p></div>}
          <div style={{background:T.surface,borderRadius:18,border:`1px solid ${T.edge}`,overflow:"hidden",marginBottom:14,boxShadow:"0 4px 20px rgba(60,40,20,0.07)"}}>
            {inviterName&&<div style={{padding:"13px 16px",background:T.panel,borderBottom:`1px solid ${T.edge}`,display:"flex",alignItems:"center",gap:10}}>
              <SUMorganAvatar size={34} name={inviterName} photo={inviterPhoto}/>
              <div><p style={{fontSize:13,fontWeight:600,color:T.ink}}>Invited by {inviterName}</p></div>
            </div>}
            <div style={{padding:16}}>
              <p style={{fontSize:11,color:T.muted,textAlign:"center",marginBottom:12}}>How did you receive this invite?</p>
              <div style={{display:"flex",flexDirection:"column",gap:7}}>
                {[
                  {id:"link",icon:"🔗",label:"Via link or text message",sub:`${name} sent you a link`},
                  {id:"email",icon:"📧",label:"Via email",sub:"Check your inbox"},
                  {id:"qr",icon:"📷",label:"Via QR code",sub:"You scanned their code"},
                ].map(entry=>(
                  <button key={entry.id} onClick={()=>handleEntry(entry.id)} style={{display:"flex",alignItems:"center",gap:12,padding:"11px 13px",borderRadius:13,background:T.mist,border:`1px solid ${T.edge}`,cursor:"pointer",textAlign:"left",transition:"all .15s"}}>
                    <span style={{fontSize:18,flexShrink:0}}>{entry.icon}</span>
                    <div style={{flex:1}}><p style={{fontSize:13,fontWeight:500,color:T.ink}}>{entry.label}</p><p style={{fontSize:11,color:T.muted}}>{entry.sub}</p></div>
                    <span style={{fontSize:16,color:T.edgeMid}}>›</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <p style={{fontSize:11,color:T.muted,textAlign:"center"}}>Orbit is free to use · Your data stays private</p>
        </div>
      </div>
    );
  }

  // ── Entry flow ──
  if(phase==="entry-flow") return(
    <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
      <div style={{padding:"8px 18px",borderBottom:`1px solid ${T.edge}`,display:"flex",alignItems:"center",gap:8,flexShrink:0}}>
        {!inviteToken&&<button onClick={()=>setPhase("entry-select")} style={{width:28,height:28,borderRadius:"50%",background:T.mist,border:"none",cursor:"pointer",color:T.muted,fontSize:13}}>←</button>}
        <span style={{fontSize:12,color:T.muted}}>{inviteToken?"Invite verified ✓":"Verifying invite"}</span>
      </div>
      <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
        {renderEntryFlow()}
      </div>
    </div>
  );

  // ── Onboarding flow ──
  return(
    <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
      <div style={{padding:"8px 16px",borderBottom:`1px solid ${T.edge}`,display:"flex",alignItems:"center",gap:5,overflowX:"auto",flexShrink:0,background:T.surface}}>
        {SU_FLOW_STEPS.map((s,i)=>{
          const active=i===flowStep, past=i<flowStep;
          return(<div key={s.id} style={{display:"flex",alignItems:"center",gap:5,flexShrink:0}}>
            <div style={{fontSize:11,padding:"2px 9px",borderRadius:9,background:active?T.partner+"18":past?T.mist:"transparent",color:active?T.partner:past?T.sage:T.muted,fontWeight:active?700:400,border:`1px solid ${active?T.partner+"40":past?T.sage+"40":"transparent"}`}}>
              {past?"✓ ":""}{s.label}
            </div>
            {i<SU_FLOW_STEPS.length-1&&<span style={{fontSize:11,color:T.edge}}>›</span>}
          </div>);
        })}
      </div>
      <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
        {renderFlowStep()}
      </div>
    </div>
  );
}
